// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {getActiveMessages} from '@github-ui/copilot-chat-helpers/copilot-chat-subthreading-helpers'
import type {DraftIssueProjectReference, DraftIssueReference} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import isEqual from 'lodash-es/isEqual'
import sortBy from 'lodash-es/sortBy'
import {useEffect} from 'react'

import {useContentPreviewBlockContext} from '../../ContentPreviewBlockContext'
import type {DraftIssue} from '../types'

/**
 * Helper function to normalize a single string value, removing all leading and trailing
 * whitespace and newlines, including escaped newlines
 * @param value - The string value to normalize
 * @returns The normalized string value
 */
const normalizeString = (value: string): string => {
  // First handle escaped newlines, then trim all whitespace including actual newlines
  return value.replace(/\\n/g, '\n').replace(/^\s+|\s+$/g, '')
}

/**
 * Helper function to compare two arrays for equality, handling order independence
 * Uses lodash utilities for robust comparison, with whitespace trimming
 * @param arr1 - First array to compare
 * @param arr2 - Second array to compare
 * @returns true if arrays contain the same elements regardless of order
 */
const arraysEqual = (arr1: readonly string[] | undefined, arr2: readonly string[] | undefined): boolean => {
  const a1 = (arr1 || []).map(item => normalizeString(item))
  const a2 = (arr2 || []).map(item => normalizeString(item))

  if (a1.length !== a2.length) return false

  // Use lodash sortBy to create sorted copies, then isEqual for comparison
  return isEqual(sortBy(a1), sortBy(a2))
}

/**
 * Helper function to compare project arrays for equality, handling different project formats
 * Prioritizes ID matching when both arrays have objects with IDs, falls back to title matching
 * when IDs are missing or when dealing with string arrays
 * @param projects1 - First project array to compare (strings or objects)
 * @param projects2 - Second project array to compare (strings or objects)
 * @returns true if arrays contain the same projects regardless of order
 */
const projectsEqual = (
  projects1: string[] | DraftIssueProjectReference[] | undefined,
  projects2: string[] | DraftIssueProjectReference[] | undefined,
): boolean => {
  const arr1 = projects1 || []
  const arr2 = projects2 || []

  if (arr1.length !== arr2.length) return false
  if (arr1.length === 0) return true

  // Check if both arrays are strings or if either array contains strings
  const hasStringProjects = arr1.some(p => typeof p === 'string') || arr2.some(p => typeof p === 'string')

  // Check if both arrays have objects with IDs
  const hasObjectsWithIds =
    !hasStringProjects &&
    arr1.every(p => typeof p === 'object' && p.id) &&
    arr2.every(p => typeof p === 'object' && p.id)

  if (hasObjectsWithIds) {
    // Both arrays have objects with IDs - compare by ID
    const ids1 = (arr1 as DraftIssueProjectReference[]).map(p => normalizeString(p.id || ''))
    const ids2 = (arr2 as DraftIssueProjectReference[]).map(p => normalizeString(p.id || ''))
    return isEqual(sortBy(ids1), sortBy(ids2))
  }

  // Fall back to title comparison for string arrays or when IDs are missing
  const getProjectTitle = (project: string | DraftIssueProjectReference): string => {
    if (typeof project === 'string') {
      return project
    }
    return project.title || ''
  }

  const titles1 = arr1.map(project => normalizeString(getProjectTitle(project)))
  const titles2 = arr2.map(project => normalizeString(getProjectTitle(project)))

  return isEqual(sortBy(titles1), sortBy(titles2))
}

/**
 * Helper function to normalize string values for comparison.
 * Treats null, undefined, and empty strings as equivalent.
 * Handles escaped newlines and strips all leading/trailing whitespace.
 * @param value - The string value to normalize
 * @returns The normalized string value
 */
const normalizeStringValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return ''
  }

  return normalizeString(value)
}

/**
 * Helper function to serialize a value to a string for comparison logging.
 * Uses normalizeStringValue for consistent string handling and ensures
 * all whitespace and newlines are properly stripped for output.
 */
const serializeValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    // Normalize each array element before joining
    const normalizedItems = value.map(item => (typeof item === 'string' ? normalizeString(item) : String(item)))
    return `[${normalizedItems.join(',')}]`
  }

  if (typeof value === 'string' || value === null || value === undefined) {
    return normalizeStringValue(value)
  }

  return JSON.stringify(value)
}

/**
 * Helper function to compare a parsed draft issue with a draft issue reference
 * to detect if they have any differences in their key fields.
 *
 * @param draftIssue - The parsed draft issue from YAML
 * @param draftIssueRef - The draft issue reference from chat message
 * @returns An object with isEqual boolean, mismatchedFields array, and field comparison details
 */
export const compareDraftIssueWithReference = (
  draftIssue: DraftIssue,
  draftIssueRef: DraftIssueReference,
): {
  isEqual: boolean
  mismatchedFields: string[]
  fieldComparisons: Record<string, {expected: string; actual: string}>
} => {
  const mismatchedFields: string[] = []
  const fieldComparisons: Record<string, {expected: string; actual: string}> = {}

  // Compare key fields to detect differences (with whitespace and newline trimming)
  const titleMatches = normalizeStringValue(draftIssue.title) === normalizeStringValue(draftIssueRef.title)
  if (!titleMatches) {
    mismatchedFields.push('title')
    fieldComparisons.title = {
      expected: serializeValue(draftIssueRef.title),
      actual: serializeValue(draftIssue.title),
    }
  }

  const bodyMatches = normalizeStringValue(draftIssue.body) === normalizeStringValue(draftIssueRef.description)
  if (!bodyMatches) {
    mismatchedFields.push('body')
    fieldComparisons.body = {
      expected: serializeValue(draftIssueRef.description),
      actual: serializeValue(draftIssue.body),
    }
  }

  const repositoryMatches =
    normalizeStringValue(draftIssue.repository) === normalizeStringValue(draftIssueRef.repository)
  if (!repositoryMatches) {
    mismatchedFields.push('repository')
    fieldComparisons.repository = {
      expected: serializeValue(draftIssueRef.repository),
      actual: serializeValue(draftIssue.repository),
    }
  }

  const templateMatches = normalizeStringValue(draftIssue.template) === normalizeStringValue(draftIssueRef.template)
  if (!templateMatches) {
    mismatchedFields.push('template')
    fieldComparisons.template = {
      expected: serializeValue(draftIssueRef.template),
      actual: serializeValue(draftIssue.template),
    }
  }

  const issueTypeMatches = normalizeStringValue(draftIssue.issueType) === normalizeStringValue(draftIssueRef.issueType)
  if (!issueTypeMatches) {
    mismatchedFields.push('issueType')
    fieldComparisons.issueType = {
      expected: serializeValue(draftIssueRef.issueType),
      actual: serializeValue(draftIssue.issueType),
    }
  }

  const milestoneMatches = normalizeStringValue(draftIssue.milestone) === normalizeStringValue(draftIssueRef.milestone)
  if (!milestoneMatches) {
    mismatchedFields.push('milestone')
    fieldComparisons.milestone = {
      expected: serializeValue(draftIssueRef.milestone),
      actual: serializeValue(draftIssue.milestone),
    }
  }

  // Use order-insensitive array comparison for arrays
  const assigneesMatch = arraysEqual(draftIssue.assignees, draftIssueRef.assignees)
  if (!assigneesMatch) {
    mismatchedFields.push('assignees')
    fieldComparisons.assignees = {
      expected: serializeValue(draftIssueRef.assignees),
      actual: serializeValue(draftIssue.assignees),
    }
  }

  const labelsMatch = arraysEqual(draftIssue.labels, draftIssueRef.labels)
  if (!labelsMatch) {
    mismatchedFields.push('labels')
    fieldComparisons.labels = {
      expected: serializeValue(draftIssueRef.labels),
      actual: serializeValue(draftIssue.labels),
    }
  }

  const projectsMatch = projectsEqual(draftIssue.projects, draftIssueRef.projects)
  if (!projectsMatch) {
    mismatchedFields.push('projects')
    fieldComparisons.projects = {
      expected: serializeValue(draftIssueRef.projects),
      actual: serializeValue(draftIssue.projects),
    }
  }

  const templateFormValuesMatch = isEqual(draftIssue.templateFormValues, draftIssueRef.templateFormValuesById)
  if (!templateFormValuesMatch) {
    mismatchedFields.push('templateFormValues')
    fieldComparisons.templateFormValues = {
      expected: serializeValue(draftIssueRef.templateFormValuesById),
      actual: serializeValue(draftIssue.templateFormValues),
    }
  }

  const blockedByTagsMatch = arraysEqual(draftIssue.blockedByTags, draftIssueRef.blockedByTags)
  if (!blockedByTagsMatch) {
    mismatchedFields.push('blockedByTags')
    fieldComparisons.blockedByTags = {
      expected: serializeValue(draftIssueRef.blockedByTags),
      actual: serializeValue(draftIssue.blockedByTags),
    }
  }

  const isBlockingTagsMatch = arraysEqual(draftIssue.isBlockingTags, draftIssueRef.isBlockingTags)
  if (!isBlockingTagsMatch) {
    mismatchedFields.push('isBlockingTags')
    fieldComparisons.isBlockingTags = {
      expected: serializeValue(draftIssueRef.isBlockingTags),
      actual: serializeValue(draftIssue.isBlockingTags),
    }
  }

  const allFieldsMatch = mismatchedFields.length === 0

  return {
    isEqual: allFieldsMatch,
    mismatchedFields,
    fieldComparisons,
  }
}

export interface UseDraftIssueReferenceValidationOptions {
  issues: DraftIssue[]
  isStreaming?: boolean
  isIssueTree?: boolean
}

/**
 * Hook that validates draft issues against their references in the corresponding assistant message.
 * Only validates issues that belong to the same message as the references.
 * Emits telemetry when mismatches are detected.
 */
export function useDraftIssueReferenceValidation({
  issues,
  isStreaming,
  isIssueTree = false,
}: UseDraftIssueReferenceValidationOptions) {
  const {messages, threadSelectedAt} = useChatState()
  const {messageId} = useContentPreviewBlockContext()

  useEffect(() => {
    const activeMessages = getActiveMessages(messages)
    const latestMessage = activeMessages[activeMessages.length - 1]

    // Find the specific message that contains this draft issue block
    const currentMessage = activeMessages.find(msg => msg.id === messageId)

    // Skip validation if still streaming, not an assistant message, message not found, or not the latest message
    if (
      isStreaming ||
      !currentMessage ||
      currentMessage.role !== 'assistant' ||
      currentMessage.id !== latestMessage?.id
    ) {
      return
    }

    // Check if message was created after current thread was selected
    const messageCreatedInCurrentChatState = new Date(currentMessage.createdAt) >= threadSelectedAt

    if (messageCreatedInCurrentChatState && currentMessage.references && issues.length > 0) {
      // Find draft issue references in the current message (not just the latest message)
      const draftIssueRefs = currentMessage.references.filter(
        (ref): ref is DraftIssueReference => ref.type === 'draft-issue' || ref.type === 'draft-update-issue',
      )

      // Check each parsed issue against references
      for (const issue of issues) {
        if (issue.tag) {
          const matchingRef = draftIssueRefs.find(ref => ref.tag === issue.tag)

          if (matchingRef) {
            const comparison = compareDraftIssueWithReference(issue, matchingRef)
            if (!comparison.isEqual) {
              sendEvent('dotcom_chat.activate', {
                target: 'BROWSER_ISSUE_DRAFT_ISSUE_REFERENCE_MISMATCH',
                mode: 'immersive',
                messageId,
                issueTree: isIssueTree,
                mismatchedFields: comparison.mismatchedFields.join(','),
                fieldComparisons: JSON.stringify(comparison.fieldComparisons),
                yamlMarshaled: isFeatureEnabled('copilot_api_agentic_issue_marshal_yaml'),
              })
              // For issue trees, don't count multiple mismatches for the same tree
              // For single issues, there's only one issue so this return doesn't matter
              return
            }
          }
        }
      }
    }
  }, [isStreaming, issues, messages, threadSelectedAt, isIssueTree, messageId])
}
